import { ILayoutBaseTestGen } from '../layoutBaseTest';
import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  ETextAlign,
  PdfUnit,
  PdfSize,
  PageGen,
  PdfPen,
  PdfColor,
  PdfPoint,
} from '../../pdf/pdf';
import '../../pdf/draw/string';
import '../../pdf/draw/primitives';
import {
  parseNotes,
  NotesCollection,
  renderFootnoteMarker,
  renderEndnoteMarker,
  Note,
} from '../../pdf/book/notes';
import { OptsBaseTest } from '../optsBaseTest';

const MM = PdfUnit.FromMillimeters;
const MARGIN = MM(12);

export class TestNotes implements ILayoutBaseTestGen {
  async generate(pdf: PdfGen, opts: OptsBaseTest): Promise<void> {
    await pdf.addStandardFonts();

    const page = pdf.addPage();
    const font = new PdfFont(PdfFont.FONT_NAME, 12);
    const titleFont = new PdfFont(PdfFont.FONT_NAME, 20);

    const titleRect = new PdfRect(MARGIN, MM(5), PdfSize.A4.width - 2 * MARGIN, MM(10));
    page.drawString(
      'Test Notes System',
      titleFont,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const testContent = `
This is a test of the notes system[^: This is a footnote].

We can have multiple footnotes[^: Second footnote] and [^: Third footnote].

We also support endnotes[~: This is an endnote at the chapter end].

Citations work too[@smith2020: Smith, J. (2020). Example Book. Publisher.].

This is another paragraph with a footnote[^: Footnote in second paragraph].
    `.trim();

    let yPos = 25;

    const rect = new PdfRect(MARGIN, MM(yPos), PdfSize.A4.width - 2 * MARGIN, MM(5));
    page.drawString(
      'Testing note parsing from:',
      font,
      PdfBrush.Black,
      null,
      rect,
      ETextAlign.CenterLeft
    );
    yPos += 8;

    const lines = testContent.split('\n');
    for (const line of lines) {
      if (line.trim()) {
        const lineRect = new PdfRect(
          MARGIN + 5,
          MM(yPos),
          PdfSize.A4.width - 2 * MARGIN - 10,
          MM(5)
        );
        page.drawString(line, font, PdfBrush.Black, null, lineRect, ETextAlign.CenterLeft);
        yPos += 6;
      }
    }

    yPos += 10;

    const notes = parseNotes(testContent);

    const notesRect = new PdfRect(MARGIN, MM(yPos), PdfSize.A4.width - 2 * MARGIN, MM(8));
    page.drawString(
      'Parsed Notes:',
      titleFont,
      PdfBrush.Black,
      null,
      notesRect,
      ETextAlign.CenterLeft
    );
    yPos += 10;

    const footnotes = notes.getFootnotes();
    page.drawString(
      `Footnotes (${footnotes.length}):`,
      font,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(yPos), MM(100), MM(5)),
      ETextAlign.CenterLeft
    );
    yPos += 8;

    for (const note of footnotes) {
      const marker = renderFootnoteMarker(note.number || 0);
      page.drawString(
        `[${marker}] ${note.content}`,
        font,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN + 10, MM(yPos), PdfSize.A4.width - 2 * MARGIN - 20, MM(5)),
        ETextAlign.CenterLeft
      );
      yPos += 6;
    }

    yPos += 5;

    const endnotes = notes.getEndnotes();
    page.drawString(
      `Endnotes (${endnotes.length}):`,
      font,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(yPos), MM(100), MM(5)),
      ETextAlign.CenterLeft
    );
    yPos += 8;

    for (const note of endnotes) {
      const marker = renderEndnoteMarker(note.number || 0);
      page.drawString(
        `[${marker}] ${note.content}`,
        font,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN + 10, MM(yPos), PdfSize.A4.width - 2 * MARGIN - 20, MM(5)),
        ETextAlign.CenterLeft
      );
      yPos += 6;
    }

    yPos += 5;

    const citations = notes.getCitations();
    page.drawString(
      `Citations (${citations.length}):`,
      font,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(yPos), MM(100), MM(5)),
      ETextAlign.CenterLeft
    );
    yPos += 8;

    for (const note of citations) {
      page.drawString(
        `[@${note.id}] ${note.content}`,
        font,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN + 10, MM(yPos), PdfSize.A4.width - 2 * MARGIN - 20, MM(5)),
        ETextAlign.CenterLeft
      );
      yPos += 6;
    }

    yPos += 15;

    const page2 = pdf.addPage();
    let yPos2 = 20;

    const page2TitleRect = new PdfRect(MARGIN, MM(yPos2), PdfSize.A4.width - 2 * MARGIN, MM(10));
    page2.drawString(
      'Footnote Rendering',
      titleFont,
      PdfBrush.Black,
      null,
      page2TitleRect,
      ETextAlign.CenterLeft
    );
    yPos2 += 15;

    page2.drawString(
      'This paragraph has a footnote[^: This footnote appears at the bottom of the page.].',
      font,
      PdfBrush.Black,
      null,
      new PdfRect(MARGIN, MM(yPos2), PdfSize.A4.width - 2 * MARGIN, MM(5)),
      ETextAlign.CenterLeft
    );
    yPos2 += 20;

    const footnoteY = PdfSize.A4.height - MM(25);
    const footnoteLineStart = new PdfPoint(MARGIN, footnoteY);
    const footnoteLineEnd = new PdfPoint(PdfSize.A4.width - MARGIN, footnoteY);
    const pen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 0.5);
    page2.drawLine(pen, footnoteLineStart, footnoteLineEnd);

    const fnRect = new PdfRect(
      MARGIN + 5,
      footnoteY - 12,
      PdfSize.A4.width - 2 * MARGIN - 10,
      MM(3)
    );
    page2.drawString(
      '1. This footnote appears at the bottom of the page.',
      new PdfFont(PdfFont.FONT_NAME, 9),
      PdfBrush.Black,
      null,
      fnRect,
      ETextAlign.CenterLeft
    );
  }
}
